// CanvasBlocksLoader.utils.js
// Utility functions for fetching canvas blocks for slides.

import Sentry from '@/services/sentry'
import SlideApi from '@/api/slide'
import { sanitizeBlocks } from '@/utils/CanvasBlocksLayout'

/**
 * Retrieves the source URL from a canvas block.
 * @param {Object} block - A canvas block object.
 * @returns {string|null} - The image URL or null if not available.
 */
function getImageBlockSrc(block) {
  if (!block || !block.content) return null

  return block.content.src
}

/**
 * Checks if the URL is a legacy CloudFront URL.
 * @param {string} url - The image URL.
 * @returns {boolean} - True if the URL contains '.cloudfront.net'.
 */
function isLegacyCloudFrontUrl(url) {
  return url && url.includes('.cloudfront.net')
}

/**
 * Checks whether the image URL has expired.
 * @param {string} url - The image URL.
 * @returns {boolean} - True if the URL is expired.
 */
function isExpiredImage(url) {
  try {
    if (!url) return false
    const imgUrl = new URL(url)
    const expires = imgUrl.searchParams.get('Expires')
    if (!expires) return true
    return expires < Date.now() / 1000
  } catch (e) {
    Sentry.captureException(e)
    return false
  }
}

/**
 * Determines if the image block needs to be re-signed.
 * @param {Object} block - A canvas block object.
 * @returns {boolean} - True if the image URL should be updated.
 */
export function isImageBlockNeedToResign(block) {
  const imgUrl = getImageBlockSrc(block)
  return isLegacyCloudFrontUrl(imgUrl) || isExpiredImage(imgUrl)
}

/**
 * Re-signs the image URLs in the canvas blocks if they have expired.
 * @param {Object} blocks - The canvas blocks object.
 * @returns {Promise<Object>} - An object containing the updated canvas blocks and a flag indicating whether the blocks should be updated.
 */
export async function resignContentSlideImageUrlsIfExpired(blocks) {
  if (!blocks) return null

  const canvasBlocksWithNewSignedUrls = { ...blocks }
  const imageBlocks = Object.values(blocks).filter((block) => block.type === 'Image')
  const imageBlocksNeedsToResign = imageBlocks.filter(isImageBlockNeedToResign)

  if (imageBlocksNeedsToResign.length === 0) {
    sanitizeBlocks(canvasBlocksWithNewSignedUrls)
    return {
      canvasBlocksWithNewSignedUrls,
      shouldUpdateCanvasBlocks: false,
    }
  }

  try {
    const arrLinkKey = imageBlocksNeedsToResign.map((block) => block.id)
    const arrayLinkValue = imageBlocksNeedsToResign
      .filter((block) => block.content && block.content.src != null)
      .map((block) => block.content.src)

    const response = await SlideApi.reSignedImages({ arrLinks: arrayLinkValue })

    if (response && response.length) {
      arrLinkKey.forEach((itemKey, index) => {
        canvasBlocksWithNewSignedUrls[`${itemKey}`].content.src = response[index]
      })
    }
  } catch (error) {
    Sentry.captureException(error)
  }

  sanitizeBlocks(canvasBlocksWithNewSignedUrls)

  return {
    canvasBlocksWithNewSignedUrls,
    shouldUpdateCanvasBlocks: true,
  }
}

/**
 * Fetch canvas blocks for a slide if the slide type is 'freestyle'.
 * @param {Object} slide - The slide object.
 * @returns {Promise<Object>} - The slide object updated with canvasBlocks, or an empty array if an error occurs.
 */
export async function fetchCanvasBlocksForSlide(slide) {
  if (slide.type !== 'freestyle') return slide

  try {
    const { body: canvasBlocks } = await SlideApi.getCanvasBlocks(slide)

    return { ...slide, canvasBlocks }
  } catch (error) {
    return { ...slide, canvasBlocks: null }
  }
}

/**
 * Fetches canvas blocks for a slide and re-signs the image URLs if they are expired.
 * This function calls the API to retrieve canvas blocks for the given slide and then uses the
 * imported helper to update the image URLs if needed.
 *
 * @param {Object} slide - The slide object for which to fetch canvas blocks.
 * @returns {Promise<Object>} - The slide object updated with the processed canvas blocks.
 */
export async function fetchAndResignCanvasBlocksImageExpired(slide) {
  if (slide.type !== 'freestyle') return slide

  try {
    const { body: canvasBlocks } = await SlideApi.getCanvasBlocks(slide)

    const {
      canvasBlocksWithNewSignedUrls,
      shouldUpdateCanvasBlocks,
    } = await resignContentSlideImageUrlsIfExpired(canvasBlocks)

    if (!shouldUpdateCanvasBlocks) return { ...slide, canvasBlocks }

    return { ...slide, canvasBlocks: canvasBlocksWithNewSignedUrls }
  } catch (error) {
    return { ...slide, canvasBlocks: null }
  }
}

/**
 * Process multiple slides concurrently to fetch their canvas blocks.
 * Uses Promise.allSettled to ensure that an error in one slide does not affect others.
 * @param {Array<Object>} slides - An array of slide objects.
 * @returns {Promise<Array<Object>>} - An array of slide objects updated with canvasBlocks.
 */
export async function fetchCanvasBlocksForSlides(slides) {
  const results = await Promise.allSettled(
    slides.map((slide) => fetchAndResignCanvasBlocksImageExpired(slide)),
  )

  return results.map((result, idx) => {
    if (result.status === 'fulfilled') return result.value

    return { ...slides[idx], canvasBlocks: null }
  })
}

/**
 * Fetches canvas blocks from a canvas blocks URL
 * @param {string} canvasBlocksUrl - The URL to fetch canvas blocks from
 * @returns {Promise<Object|null>} - The canvas blocks object or null if failed
 */
export async function fetchCanvasBlocksFromUrl(canvasBlocksUrl) {
  if (!canvasBlocksUrl) return null

  try {
    const body = await fetch(canvasBlocksUrl).then((res) => res.json())
    // If the parsed body is falsy (e.g., null, undefined), return null.
    // Otherwise, return the body.
    return body || null
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}

/**
 * Updates canvas blocks for a slide by fetching from URL and dispatching update action
 * @param {string} newCanvasBlocksUrl - The URL to fetch new canvas blocks from
 * @param {Function} dispatch - Vuex dispatch function
 * @param {string} slideId - The slide ID to update
 * @param {string} updateAction - The action name to dispatch (default: 'updateSlideByIdAction')
 * @returns {Promise<void>}
 */
export async function updateCanvasBlocksFromUrl(newCanvasBlocksUrl, dispatch, slideId, updateAction = 'updateSlideByIdAction') {
  if (!newCanvasBlocksUrl) return

  const newCanvasBlocks = await fetchCanvasBlocksFromUrl(newCanvasBlocksUrl)
  await dispatch(updateAction, {
    id: slideId,
    canvasBlocks: newCanvasBlocks,
    canvasBlocksUrl: newCanvasBlocksUrl,
  }, { root: true })
}
